import React, { useState, useEffect, useCallback } from 'react';
import { externalApi } from '../../services/api';
import { formatINR } from '../../utils/formatters';
import { ArrowUp, ArrowDown, Warning } from '@phosphor-icons/react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from 'recharts';

const SYMBOLS = ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'RELIANCE', 'TCS', 'HDFCBANK'];

function pcrSentiment(pcr) {
  if (pcr == null) return { color: '#94A3B8', label: 'N/A' };
  if (pcr > 1.3) return { color: '#10B981', label: 'Bullish (heavy PE writing)' };
  if (pcr < 0.7) return { color: '#EF4444', label: 'Bearish (heavy CE writing)' };
  return { color: '#F59E0B', label: 'Neutral' };
}

export default function OptionsAnalyticsTab() {
  const [symbol, setSymbol] = useState('NIFTY');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async (s) => {
    setLoading(true);
    try {
      const resp = await externalApi.options(s);
      setData(resp.data);
    } catch (e) {
      console.error('options fetch failed', e);
      setData({ error: 'Failed to fetch options data' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(symbol); }, [symbol, fetchData]);

  return (
    <div className="space-y-6" data-testid="options-tab-content">
      <SymbolPicker current={symbol} onChange={setSymbol} />

      {loading && <div className="text-[#64748B] text-sm">Loading options analytics…</div>}

      {!loading && data && (
        <>
          {data.data_source === 'synthetic_fallback' && <FallbackBanner />}
          <StatsRow data={data} />
          <SupportsResistances data={data} />
          {data.pain_curve?.length > 0 && <PainCurve data={data} />}
        </>
      )}
    </div>
  );
}

function SymbolPicker({ current, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {SYMBOLS.map((s) => (
        <button
          key={s}
          onClick={() => onChange(s)}
          className={`px-3 py-1.5 text-xs border transition-all ${
            current === s
              ? 'border-[#2563EB] bg-[#2563EB]/10 text-white'
              : 'border-white/10 text-[#94A3B8] hover:border-white/30'
          }`}
          data-testid={`options-symbol-${s}`}
        >
          {s}
        </button>
      ))}
    </div>
  );
}

function FallbackBanner() {
  return (
    <div className="p-3 border border-[#F59E0B]/30 bg-[#F59E0B]/10 text-[#F59E0B] text-xs">
      <Warning size={14} weight="fill" className="inline mr-1.5" />
      NSE India blocks datacenter IPs — showing computed/synthetic options analytics.
      PCR & Max Pain are calculated from modelled OI distribution.
    </div>
  );
}

function StatsRow({ data }) {
  const sentiment = pcrSentiment(data.pcr_oi);
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatCard
        label="Underlying"
        value={data.underlying_value ? formatINR(data.underlying_value) : '—'}
        testid="opts-underlying"
      />
      <StatCard
        label="PCR (OI)"
        value={data.pcr_oi?.toFixed(3) ?? '—'}
        subtext={sentiment.label}
        color={sentiment.color}
        testid="opts-pcr-oi"
      />
      <StatCard
        label="Max Pain"
        value={data.max_pain ? formatINR(data.max_pain) : '—'}
        testid="opts-max-pain"
      />
      <StatCard label="Expiry" value={data.current_expiry || '—'} testid="opts-expiry" />
    </div>
  );
}

function StatCard({ label, value, subtext, color = '#FFFFFF', testid }) {
  return (
    <div className="p-3 border border-white/10 bg-white/[0.02]" data-testid={testid}>
      <div className="text-[10px] text-[#64748B] uppercase tracking-wide">{label}</div>
      <div className="text-lg font-mono font-bold mt-1" style={{ color }}>{value}</div>
      {subtext && <div className="text-[10px] text-[#94A3B8] mt-0.5">{subtext}</div>}
    </div>
  );
}

function SupportsResistances({ data }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <StrikeList
        title={<><ArrowDown size={12} className="inline mr-1" /> Top Support Strikes (Max PE OI)</>}
        titleColor="text-[#10B981]"
        rows={data.supports || []}
        oiKey="pe_oi"
        oiColor="text-[#10B981]"
        testidPrefix="opts-support"
      />
      <StrikeList
        title={<><ArrowUp size={12} className="inline mr-1" /> Top Resistance Strikes (Max CE OI)</>}
        titleColor="text-[#EF4444]"
        rows={data.resistances || []}
        oiKey="ce_oi"
        oiColor="text-[#EF4444]"
        testidPrefix="opts-resistance"
      />
    </div>
  );
}

function StrikeList({ title, titleColor, rows, oiKey, oiColor, testidPrefix }) {
  return (
    <div>
      <p className={`text-[10px] uppercase tracking-[0.2em] mb-3 ${titleColor}`}>{title}</p>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div
            key={r.strike}
            className="flex justify-between items-center p-2.5 border border-white/10 bg-white/[0.02]"
            data-testid={`${testidPrefix}-${r.strike}`}
          >
            <span className="font-mono text-white">{formatINR(r.strike)}</span>
            <span className={`text-xs ${oiColor} font-mono`}>
              {oiKey.toUpperCase().replace('_', ' ')}: {(r[oiKey] || 0).toLocaleString('en-IN')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PainCurve({ data }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-3">Max Pain Curve</p>
      <div className="border border-white/10 p-3 bg-white/[0.02]" style={{ height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data.pain_curve}>
            <CartesianGrid stroke="#1F2937" strokeDasharray="3 3" />
            <XAxis dataKey="strike" stroke="#64748B" fontSize={10} />
            <YAxis stroke="#64748B" fontSize={10} />
            <Tooltip
              contentStyle={{ background: '#0A0B0E', border: '1px solid #1F2937', fontSize: 12 }}
              formatter={(v) => v.toLocaleString('en-IN')}
            />
            <Bar dataKey="pain" fill="#2563EB" opacity={0.7} />
            {data.max_pain && (
              <ReferenceLine
                x={data.max_pain}
                stroke="#F59E0B"
                strokeDasharray="4 2"
                label={{ value: 'Max Pain', fill: '#F59E0B', fontSize: 10 }}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
